import { storeToRefs } from 'pinia';
import { useUserStore } from '@/store/userStore';

/**
 * Build the public transcript-application URL the same way the public route
 * resolves it: `{origin}/transcripts/<slug>` (route `public-transcript-request`,
 * `src/router/index.ts`). Returns null when the school has no slug yet so callers
 * can hide the affordance rather than render a broken link.
 */
export function buildPublicTranscriptUrl(
  slug: string | null | undefined,
  origin: string = window.location.origin,
): string | null {
  if (!slug) return null;
  return `${origin}/transcripts/${slug}`;
}

/**
 * Exposes the current school's slug and its public transcript URL for the admin
 * Transcript area, derived from the active school in the user store. The link is
 * built on the school's own public host (`School.frontend_url`: custom domain,
 * tenant subdomain, or the platform URL) — never on the admin's current origin,
 * which on the platform host would hand a white-labelled school a GradeVerse link.
 */
export function usePublicTranscriptLink() {
  const { currentSchool } = storeToRefs(useUserStore());

  const slug = computed(() => currentSchool.value?.slug ?? null);
  const origin = computed(() => currentSchool.value?.frontend_url || window.location.origin);
  const publicTranscriptUrl = computed(() => buildPublicTranscriptUrl(slug.value, origin.value));

  return { slug, publicTranscriptUrl };
}
